import {get, put, post} from 'utils/apiClient';
import React, {createContext, useState} from 'react';

const NotificationContext = createContext();

function Provider({ children }) {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)
    const [notificationConfig, setNotificationConfig] = useState(null)
    const [configType, setConfigType] = useState('rulebased')
    const [selectedRule, setSelectedRule] = useState('none')
    const [hasQconRule, setHasQconRule] = useState(false)
    // vars other than notif_rule_for_exp (which is auto-generated)
    const [additionalVars, setAdditionalVars] = useState([])

    const RULE_DETECTION_ORDER = [
        { key: 'customer_id',    match: val => val.includes('ord_cust_id') },
        { key: 'Revenue_code',   match: val => val.includes("PATHVAL('ext_attrbs.ordRevCode')") || val.includes("ext_attrbs.get('ordRevCode')") || val.includes('ordRevCode') },
        { key: 'location_id',    match: val => val.includes('t_loc_id') },
        { key: 'master_location_uid',  match: val => val.includes('m_loc_uid') },
        { key: 'customer_name',  match: val => val.includes('ord_cust_nm') },
        { key: 'csr',            match: val => val.includes("PATHVAL('ext_attrbs.tms_additional_attribs.CSR')") || val.includes("'CSR'") },
        { key: 'operations_user', match: val => val.includes("PATHVAL('ext_attrbs.tms_additional_attribs.operationsUser')") || val.includes('operationsUser') },
        { key: 'appt_sys_ref',    match: val => val.includes('appt_sys_ref') },
        { key: 'appt_req_user_dept', match: val => val.includes('appt_reqstd_usr_dept') },
    ];

    const detectRuleFromVars = (vars) => {
        for (const v of vars) {
            if (v.name !== 'notif_rule_for_exp') continue;
            const val = v.value || '';
            // f-strings or 3+ arguments = complex custom expression
            if (val.includes("f'") || val.includes('f"')) return 'unknown';
            const inner = val.match(/EMAIL_GRP_BY_KEY\((.+)\)/s);
            if (inner && inner[1].split(',').length > 2) return 'unknown';
            for (const rule of RULE_DETECTION_ORDER) {
                if (rule.match(val)) return rule.key;
            }
        }
        return 'unknown';
    };

    const loadNotificationConfig = async () => {
        setLoading(true)
        try{
            const data = await get('notification');
            console.log('Notification Config Response:', data);
            if(data) {
                setNotificationConfig(data);
                if(data.vars && data.email_cfg) {
                    setConfigType('rulebased');
                    const extraVars = data.vars.filter(v => v.name !== 'notif_rule_for_exp');
                    setAdditionalVars(extraVars);
                    setHasQconRule(extraVars.some(v => v.name === 'notif_rule_for_qcon'));
                    if(data.vars.length > 0) {
                        const hasNonDefaultGroups = data.email_cfg.some(config =>
                            config.grps && config.grps.some(grp => grp !== 'DEFAULT')
                        );
                        if(hasNonDefaultGroups) {
                            setSelectedRule(detectRuleFromVars(data.vars));
                        } else {
                            setSelectedRule('none');
                        }
                    } else {
                        setSelectedRule('none');
                    }
                } else if(data.to || data.cc || data.default_to || data.default_cc || data.file_to || data.file_cc) {
                    setConfigType('plain');
                }
            } else {
                setNotificationConfig(null);
            }
            setLoading(false)
        } catch (error){
            console.error('Notification config load error:', error);
            setLoading(false)
            setError(true)
            setNotificationConfig(null);
        }
    };

    const hasDefaultEmails = (config) => {
        if (!config || !config.email_cfg) return false;
        
        const defaultConfig = config.email_cfg.find(cfg => cfg.grps && cfg.grps.includes('DEFAULT'));
        if (!defaultConfig || !defaultConfig.emails) return false;
        
        const hasTo = defaultConfig.emails.to && (
            (Array.isArray(defaultConfig.emails.to) && defaultConfig.emails.to.length > 0) ||
            (typeof defaultConfig.emails.to === 'string' && defaultConfig.emails.to.trim() !== '')
        );
        
        const hasCc = defaultConfig.emails.cc && (
            (Array.isArray(defaultConfig.emails.cc) && defaultConfig.emails.cc.length > 0) ||
            (typeof defaultConfig.emails.cc === 'string' && defaultConfig.emails.cc.trim() !== '')
        );
        
        return hasTo || hasCc;
    };

    const hasEmailConfigs = (config) => {
        if (!config || !config.email_cfg) return false;
        
        const nonDefaultConfigs = config.email_cfg.filter(cfg => 
            !cfg.grps || !cfg.grps.includes('DEFAULT')
        );
        
        return nonDefaultConfigs.length > 0;
    };

    const BASE_RULE_EXPRS = {
        'customer_id':   'ord_cust_id',
        'Revenue_code':  "PATHVAL('ext_attrbs.ordRevCode')",
        'location_id':   't_loc_id',
        'master_location_uid': 'm_loc_uid',
        'customer_name': 'ord_cust_nm',
        'csr':           "PATHVAL('ext_attrbs.tms_additional_attribs.CSR')",
        'operations_user': "PATHVAL('ext_attrbs.tms_additional_attribs.operationsUser')",
        'appt_sys_ref':    'appt_sys_ref',
        'appt_req_user_dept': "PATHVAL('ext_attrbs.tms_additional_attribs.appt_reqstd_usr_dept')",
    };

    const getVarsValueForRule = (ruleType, hasDefaultEmails = false, hasEmailConfigs = false) => {
        if (ruleType === 'none') {
            ruleType = 'customer_id';
        }
        const baseRule = BASE_RULE_EXPRS[ruleType] || BASE_RULE_EXPRS['customer_id'];

        if (hasDefaultEmails && !hasEmailConfigs) {
            return "EMAIL_GRP_BY_KEY('DEFAULT')";
        }
        else if (!hasDefaultEmails && hasEmailConfigs) {
            return `EMAIL_GRP_BY_KEY(${baseRule})`;
        }
        else if (hasDefaultEmails && hasEmailConfigs) {
            return `EMAIL_GRP_BY_KEY(${baseRule}, 'DEFAULT')`;
        }
        else {
            return `EMAIL_GRP_BY_KEY(${baseRule})`;
        }
    };

    const getQconVarsValue = (ruleType, config) => {
        const ruleKey = ruleType === 'none' ? 'customer_id' : ruleType;
        const baseRule = BASE_RULE_EXPRS[ruleKey] || BASE_RULE_EXPRS['customer_id'];
        const nonDefaultGroups = (config.email_cfg || [])
            .filter(cfg => !cfg.grps?.includes('DEFAULT'))
            .flatMap(cfg => cfg.grps || []);
        const groupsStr = nonDefaultGroups.map(g => `'${g}'`).join(', ');
        return `EMAIL_GRP_BY_KEY(${baseRule} if ${baseRule} in (${groupsStr}) else None)`;
    };

    const updateNotificationConfig = async (config) => {
        setLoading(true)
        try{
            let updatedConfig;
            if (configType === 'plain') {
                updatedConfig = config;
            } else {
                // If the caller already built the vars (e.g. NotificationConfig saving a routing rule),
                // use them directly. Otherwise preserve existing vars — never auto-generate
                // a notif_rule_for_exp that the user didn't configure.
                const configHasNotifRule = config.vars?.some(v => v.name === 'notif_rule_for_exp');
                let vars;
                if (configHasNotifRule) {
                    vars = config.vars;
                } else {
                    const existingNotifExpr = notificationConfig?.vars?.find(v => v.name === 'notif_rule_for_exp');
                    if (existingNotifExpr) {
                        // Preserve the existing rule the user previously configured
                        const hasDefaults = hasDefaultEmails(config);
                        const hasConfigs = hasEmailConfigs(config);
                        const notifRuleValue = selectedRule === 'unknown'
                            ? existingNotifExpr.value
                            : getVarsValueForRule(selectedRule, hasDefaults, hasConfigs);
                        vars = [
                            {
                                name: "notif_rule_for_exp",
                                type: "EXPR",
                                notes: existingNotifExpr.notes || "EMAIL selection rule",
                                value: notifRuleValue
                            },
                            ...additionalVars
                        ];
                    } else {
                        // No existing rule — don't auto-generate one, just keep additional vars
                        vars = [...additionalVars];
                    }
                }
                updatedConfig = { ...config, vars: vars.length > 0 ? vars : undefined };
            }

            console.log('Updating config with rule:', selectedRule);
            console.log('Has qcon rule:', hasQconRule);
            console.log('Updated config:', updatedConfig);

            const response = await put('notification', updatedConfig);
            console.log('Update response:', response);

            if(response) {
                setNotificationConfig(updatedConfig);
                if(updatedConfig.vars && updatedConfig.email_cfg) {
                    setHasQconRule(updatedConfig.vars.some(v => v.name === 'notif_rule_for_qcon'));
                    const hasNonDefaultGroups = updatedConfig.email_cfg.some(config =>
                        config.grps && config.grps.some(grp => grp !== 'DEFAULT')
                    );
                    if(hasNonDefaultGroups) {
                        setSelectedRule(detectRuleFromVars(updatedConfig.vars));
                    } else {
                        setSelectedRule('none');
                    }
                }
                setLoading(false)
                return true;
            } else {
                console.error('Failed to update notification config');
                setError(true);
                setLoading(false)
                return false;
            }
        } catch (error){
            console.error('Update error:', error);
            setLoading(false)
            setError(true)
            return false;
        }
    };

    const canEditConfiguration = () => {
        if (!notificationConfig || !notificationConfig.vars || notificationConfig.vars.length === 0) {
            return true;
        }

        const hasValidRule = notificationConfig.vars.some(v => {
            const val = v?.value || '';
            return val.includes('ord_cust_id') || val.includes("PATHVAL('ext_attrbs.ordRevCode')") || val.includes("ext_attrbs.get('ordRevCode')") || val.includes("DEFAULT");
        });

        return hasValidRule;
    };

    const createNotificationConfig = async (config) => {
        setLoading(true)
        try{
            const hasDefaults = hasDefaultEmails(config);
            const hasConfigs = hasEmailConfigs(config);

            const createVars = [
                {
                    name: "notif_rule_for_exp",
                    type: "EXPR",
                    notes: "EMAIL selection rule",
                    value: getVarsValueForRule(selectedRule, hasDefaults, hasConfigs)
                },
                ...additionalVars
            ];
            const configWithVars = { ...config, vars: createVars };

            console.log('Creating config with rule:', selectedRule);
            console.log('Has default emails:', hasDefaults);
            console.log('Has email configs:', hasConfigs);
            console.log('Has qcon rule:', hasQconRule);
            console.log('Config with vars:', configWithVars);

            const response = await post('notification', configWithVars);
            console.log('Create response:', response);
            
            if(response) {
                setNotificationConfig(configWithVars);
                setLoading(false)
                return true;
            } else {
                console.error('Failed to create notification config');
                setError(true);
                setLoading(false)
                return false;
            }
        } catch (error){
            console.error('Create error:', error);
            setLoading(false)
            setError(true)
            return false;
        }
    };

    const valueToShare = {
        loading, setLoading,
        error, setError,
        notificationConfig, setNotificationConfig,
        configType, setConfigType,
        selectedRule, setSelectedRule,
        hasQconRule, setHasQconRule,
        additionalVars, setAdditionalVars,
        loadNotificationConfig,
        updateNotificationConfig,
        createNotificationConfig,
        canEditConfiguration
    }
    
    return (
        <NotificationContext.Provider value={valueToShare}>
            {children}
        </NotificationContext.Provider>
    )
}

export { Provider }
export default NotificationContext